import getBrowserSystemInfo from './KSIBrowserInfo';
import invokeBrowserPubSub, { canUseBrowserPubSub, isBrowserPubSubAPI } from './KSIBrowserPubSub';
import {
  getSafeMessageContext, InvokeMessage, isRecord, parseMessage,
} from './KSIMessage';

declare const require: (id: string) => any;

export interface Spec {
  invoke: (msg: string, callback: (result: string) => void) => void;
  invokeSync: (msg: string) => string;
  setMapping: (mapping: { interfaces?: Record<string, MappingItem | MappingItem[]>; }) => void;
}
interface InvokeResult {
  type: 'return' | 'callback';
  callbackId: number;
  code: number;
  msg: string;
  data: Record<string, any>;
  errno: number;
  uiData?: Record<string, any>;
  innerData?: Record<string, any>;
  metrics?: Record<string, number>;
}
interface MappingItem {
  mpMethod?: string | string[] | Record<string, string | string[]>;
  knbMethod?: string;
  mpParamLayer?: 'map' | 'flat';
  params?: string[];
  callback?: string;
  platform?: 'android' | 'ios';
  scope?: string;
}
interface InvocationTarget {
  callbackBased: boolean;
  supportsSync: boolean;
  call: (resolve: (value?: any) => void, reject: (reason?: any) => void) => any;
}
const CODE_SUCCESS = 200;
const CODE_BAD_REQUEST = 400;
const CODE_NOT_FOUND = 404;
const CODE_EXECUTION_FAILED = 405;
const CODE_INTERNAL_ERROR = 500;
const CODE_TIMEOUT = 504;
const ERRNO_OK = 0;
const ERRNO_UNKNOWN = -1;
const ERRNO_INVALID_PARAMS = 29999;
const ERRNO_INVALID_MESSAGE = 57895;
const ERRNO_UNSUPPORTED = 57996;
const ASYNC_TIMEOUT = 30000;
const NO_INTERNAL_HANDLER = Symbol('NO_INTERNAL_HANDLER');
const MODULE_ALIASES: Record<string, string[]> = {
  Compass: ['SLCompass'],
  Map: ['SLMapModule'],
  SLMap: ['SLMapModule'],
  SLHorn: ['SLCHorn'],
  SOAToast: ['Toast', 'ActivityIndicator'],
};
const METHOD_ALIASES: Record<string, string[]> = {
  currentCompassInfo: ['getCompassInfo'],
  removeLoading: ['hideLoading'],
};
const KNB_METHOD_ALIASES: Record<string, string> = {
  'sailor.login': 'login',
};
const SYNC_CAPABLE_CALLBACK_METHODS = new Set([
  'Router.navigateBack', 'SLCommon.openURL', 'SLMPCache.getCacheSize',
]);
let mappingCache: Record<string, MappingItem | MappingItem[]> | null = null;
function setMapping(mapping: { interfaces?: Record<string, MappingItem | MappingItem[]>; } = {}): void {
  const { interfaces } = mapping;
  mappingCache = interfaces && typeof interfaces === 'object' ? interfaces : {};
}
function getGlobalObject(): any {
  if (typeof globalThis !== 'undefined') return globalThis;
  if (typeof window !== 'undefined') return window;
  return {};
}
function getMach(): any {
  return getGlobalObject().Mach;
}
function getKNB(): any {
  return getGlobalObject().KNB;
}
function getMapping(): Record<string, MappingItem | MappingItem[]> {
  if (mappingCache) return mappingCache;
  try {
    // Lazy loading avoids a cycle while @sailor/ksi-machpro is obtaining KSIModule.
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires, import/no-extraneous-dependencies
    const ksiMachPro = require('@sailor/ksi-machpro');
    const interfaces = ksiMachPro?.mapping?.interfaces;
    if (interfaces && typeof interfaces === 'object') {
      mappingCache = interfaces;
      return interfaces;
    }
  } catch (error) {
    // The adapter can be absent for bundles that only use the base Mach runtime.
  }
  return {};
}
function getErrorMessage(error: any): string {
  if (typeof error === 'string' && error) return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error.message === 'string') return error.message;
  if (error && typeof error.errMsg === 'string') return error.errMsg;
  if (error && typeof error.msg === 'string') return error.msg;
  return String(error || 'Unknown error');
}
function getNumericErrorField(error: any, fields: string[], fallback: number): number {
  if (!error || typeof error !== 'object') return fallback;
  for (const field of fields) {
    const value = error[field];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return fallback;
}
function normalizeData(value: any): Record<string, any> {
  if (typeof value === 'undefined') return {};
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { ...value };
  }
  return { result: value };
}
function normalizeErrorData(error: any): Record<string, any> {
  if (!error || typeof error !== 'object') return {};
  return {
    ...error,
    message: getErrorMessage(error),
  };
}
function createResult(
  message: Partial<InvokeMessage>,
  type: InvokeResult['type'],
  value?: any,
  error?: any,
  errorCode = CODE_EXECUTION_FAILED,
  errno = ERRNO_UNKNOWN,
): InvokeResult {
  const failed = typeof error !== 'undefined';
  return {
    type,
    callbackId: typeof message.callbackId === 'number' ? message.callbackId : -1,
    code: failed ? getNumericErrorField(error, ['code', 'errCode'], errorCode) : CODE_SUCCESS,
    msg: failed
      ? `${message.name || 'unknown'}:fail ${getErrorMessage(error)}`
      : `${message.name || 'unknown'}:ok`,
    data: failed ? normalizeErrorData(error) : normalizeData(value),
    errno: failed ? getNumericErrorField(error, ['errno', 'errNo'], errno) : ERRNO_OK,
    uiData: message.uiArgs,
    innerData: failed ? { ...(message.innerArgs || {}), errorLevel: 2 } : message.innerArgs || {},
    metrics: message.metrics || {},
  };
}
function serialize(value: InvokeResult): string {
  try {
    const seen = new WeakSet();
    return JSON.stringify(value, (_key, item) => {
      if (typeof item === 'bigint') return item.toString();
      if (typeof item === 'function' || typeof item === 'symbol') return undefined;
      if (item && typeof item === 'object') {
        if (seen.has(item)) return '[Circular]';
        seen.add(item);
      }
      return item;
    });
  } catch (error) {
    return JSON.stringify({
      type: value.type,
      callbackId: value.callbackId,
      code: CODE_INTERNAL_ERROR,
      msg: `unknown:fail ${getErrorMessage(error)}`,
      data: {},
      errno: ERRNO_UNKNOWN,
    });
  }
}
function serializeResult(result: InvokeResult): string {
  return serialize(result);
}
function getMappingItems(message: Pick<InvokeMessage, 'name' | 'scope'>): MappingItem[] {
  const mapping = getMapping();
  const config = mapping[message.name] || mapping[message.name.replace(/Sync$/, '')];
  if (!config) return [];
  const expectedScope = message.scope || 'ksi.default';
  const items = (Array.isArray(config) ? config : [config])
    .filter((item) => !item.scope || item.scope === expectedScope);
  const mach = getMach();
  let platform: MappingItem['platform'];
  if (mach?.env?.isAndroid) platform = 'android';
  if (mach?.env?.isIOS) platform = 'ios';
  const getPlatformScore = (item: MappingItem) => {
    if (!item.platform) return 1;
    return item.platform === platform ? 2 : 0;
  };
  return [...items].sort((left, right) => {
    return getPlatformScore(right) - getPlatformScore(left);
  });
}
function getMethodPaths(item: MappingItem, args: Record<string, any>): string[] {
  const method = item.mpMethod;
  if (typeof method === 'string') return [method];
  if (Array.isArray(method)) return method;
  if (!method || typeof method !== 'object') return [];

  let typeKey = 'string';
  if (Object.prototype.hasOwnProperty.call(args, 'value')) {
    typeKey = Array.isArray(args.value) ? 'array' : typeof args.value;
  } else if (Object.prototype.hasOwnProperty.call(args, 'dataType')) {
    typeKey = String(args.dataType);
  }
  const selected = method[typeKey];
  if (typeof selected === 'string') return [selected];
  if (Array.isArray(selected)) return selected;
  return Object.values(method).flatMap((value) => (Array.isArray(value) ? value : [value]));
}
function resolveMachMethod(path: string): { owner: any; method: (...args: any[]) => any; } | null {
  const dotIndex = path.indexOf('.');
  if (dotIndex <= 0) return null;
  const moduleName = path.slice(0, dotIndex);
  const methodName = path.slice(dotIndex + 1);
  const mach = getMach();
  if (!mach?.requireModule) return null;

  const moduleNames = [moduleName, ...(MODULE_ALIASES[moduleName] || [])];
  const methodNames = [methodName, ...(METHOD_ALIASES[methodName] || [])];
  for (const candidateModuleName of moduleNames) {
    let moduleInstance;
    try {
      moduleInstance = mach.requireModule(candidateModuleName);
    } catch (error) {
      moduleInstance = null;
    }
    for (const candidateMethodName of methodNames) {
      if (typeof moduleInstance?.[candidateMethodName] === 'function') {
        return { owner: moduleInstance, method: moduleInstance[candidateMethodName] };
      }
    }
  }
  return null;
}
function buildMachArgs(item: MappingItem, args: Record<string, any>): any[] {
  const params = item.params || [];
  if (params.length === 0) return [];
  if (item.mpParamLayer === 'map') {
    const mapped = params.reduce((result, spec) => {
      const [key, alias = key] = spec.split(':');
      if (Object.prototype.hasOwnProperty.call(args, key)) result[alias] = args[key];
      return result;
    }, {} as Record<string, any>);
    return [Object.keys(mapped).length > 0 ? mapped : null];
  }
  const values = params.map((spec) => {
    const key = spec.split(':')[0];
    return Object.prototype.hasOwnProperty.call(args, key) ? args[key] : undefined;
  });
  while (values.length > 0 && typeof values[values.length - 1] === 'undefined') values.pop();
  return values;
}
function createMachTarget(item: MappingItem, message: InvokeMessage): InvocationTarget | null {
  const args = message.args || {};
  for (const path of getMethodPaths(item, args)) {
    const resolved = resolveMachMethod(path);
    if (resolved) {
      const methodArgs = buildMachArgs(item, args);
      const callbackBased = Boolean(item.callback);
      return {
        callbackBased,
        supportsSync: !callbackBased || SYNC_CAPABLE_CALLBACK_METHODS.has(path),
        call(resolve) {
          const invocationArgs = [...methodArgs];
          if (callbackBased) invocationArgs.push(resolve);
          return resolved.method.apply(resolved.owner, invocationArgs);
        },
      };
    }
  }
  return null;
}
function createKNBTarget(item: MappingItem, message: InvokeMessage): InvocationTarget | null {
  if (!item.knbMethod) return null;
  const knb = getKNB();
  if (!knb) return null;
  const methodName = KNB_METHOD_ALIASES[item.knbMethod] || item.knbMethod;
  if (typeof knb[methodName] !== 'function') return null;
  return {
    callbackBased: true,
    supportsSync: false,
    call(resolve, reject) {
      const params = { ...(message.args || {}), success: resolve, fail: reject };
      if (typeof knb.use === 'function') return knb.use(item.knbMethod, params);
      return knb[methodName](params);
    },
  };
}
function resolveTarget(message: InvokeMessage): InvocationTarget | null {
  const items = getMappingItems(message);
  for (const item of items) {
    const target = createMachTarget(item, message) || createKNBTarget(item, message);
    if (target) return target;
  }
  return null;
}
function canIUse(args: Record<string, any>): boolean {
  const schema = typeof args.schema === 'string' ? args.schema : '';
  const [name, mode, ...fields] = schema.split('.');
  if (!name) return false;
  if (name === 'canIUse') return !mode;
  if (isBrowserPubSubAPI(name)) return canUseBrowserPubSub(name, mode, fields, getMach());
  if (['getSystemInfo', 'getSystemInfoAsync', 'getSystemInfoSync'].includes(name)) {
    if (!mode) return true;
    if (mode === 'param') return fields.length === 1 && fields[0] === '_mt';
    if (mode !== (name.endsWith('Sync') ? 'return' : 'success')) return false;
    if (fields.length === 0) return true;
    let value: any = getBrowserSystemInfo(getGlobalObject(), getMach());
    return fields.every((field) => {
      if (!isRecord(value) || !Object.prototype.hasOwnProperty.call(value, field)) return false;
      value = value[field];
      return true;
    });
  }
  const message = {
    scope: typeof args.scope === 'string' ? args.scope : 'ksi.default',
    name,
    args: {},
    callbackId: -1,
  };
  const target = resolveTarget(message);
  if (!target || (name.endsWith('Sync') && !target.supportsSync)) return false;
  if (!mode) return true;
  if (mode === (name.endsWith('Sync') ? 'return' : 'success')) return fields.length === 0;
  if (mode !== 'param' || fields.length !== 1) return false;
  return getMappingItems(message).some((item) => (
    item.params || []
  ).some((spec) => spec.split(':')[0] === fields[0]));
}
function reportKSIMetrics(args: Record<string, any>): void {
  if (!Array.isArray(args.metrics)) return;
  let raptor: any;
  try {
    raptor = getMach()?.requireModule?.('SLRaptor');
  } catch (error) {
    return;
  }
  if (typeof raptor?.reportMetrics !== 'function') return;
  args.metrics.forEach((metric) => {
    if (!metric || typeof metric.type !== 'string') return;
    if (typeof metric.value !== 'number' && typeof metric.value !== 'string') return;
    raptor.reportMetrics({ [metric.type]: metric.value }, {
      ...(metric.tags || {}),
      ...(metric.details || {}),
      ...(metric.category ? { category: metric.category } : {}),
    });
  });
}
function invokeInternal(message: InvokeMessage): any | typeof NO_INTERNAL_HANDLER {
  switch (message.name) {
    case 'subscribe':
    case 'unsubscribe':
    case 'publish':
      return invokeBrowserPubSub(message, getMach());
    case 'canIUse':
    case 'canIUseAsync':
      return { result: canIUse(message.args || {}) };
    case 'getSystemInfo':
    case 'getSystemInfoAsync':
    case 'getSystemInfoSync':
      return getBrowserSystemInfo(getGlobalObject(), getMach());
    case 'registerMsiFeConfigChange':
      return {};
    case 'reportKSIMetrics':
      reportKSIMetrics(message.args || {});
      return {};
    default:
      return NO_INTERNAL_HANDLER;
  }
}
function invoke(msg: string, callback: (result: string) => void): void {
  let message: InvokeMessage;
  try {
    message = parseMessage(msg);
  } catch (error) {
    const isMalformedJSON = error instanceof SyntaxError || (error as any)?.name === 'SyntaxError';
    callback?.(serializeResult(createResult(
      getSafeMessageContext(msg),
      'callback',
      undefined,
      error,
      isMalformedJSON ? CODE_INTERNAL_ERROR : CODE_BAD_REQUEST,
      isMalformedJSON ? ERRNO_INVALID_MESSAGE : ERRNO_INVALID_PARAMS,
    )));
    return;
  }
  let settled = false;
  let timer: ReturnType<typeof setTimeout>;
  const finish = (
    value?: any,
    error?: any,
    errorCode = CODE_EXECUTION_FAILED,
    errno = ERRNO_UNKNOWN,
  ) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    callback?.(serializeResult(createResult(message, 'callback', value, error, errorCode, errno)));
  };
  timer = setTimeout(() => {
    finish(
      undefined,
      new Error(`KSI browser invocation timed out after ${ASYNC_TIMEOUT}ms`),
      CODE_TIMEOUT,
    );
  }, ASYNC_TIMEOUT);

  try {
    const internalValue = invokeInternal(message);
    if (internalValue !== NO_INTERNAL_HANDLER) {
      finish(internalValue);
      return;
    }
    const target = resolveTarget(message);
    if (!target) {
      finish(
        undefined,
        new Error(`KSI API ${message.scope || 'ksi.default'}.${message.name} is not supported in browser`),
        CODE_NOT_FOUND,
        ERRNO_UNSUPPORTED,
      );
      return;
    }
    const returnValue = target.call((value) => finish(value), (error) => finish(undefined, error));
    if (returnValue && typeof returnValue.then === 'function') {
      returnValue.then((value: any) => finish(value), (error: any) => finish(undefined, error));
    } else if (!target.callbackBased) {
      finish(returnValue);
    }
  } catch (error) {
    finish(undefined, error);
  }
}
function invokeSync(msg: string): string {
  let message: InvokeMessage;
  try {
    message = parseMessage(msg);
  } catch (error) {
    const isMalformedJSON = error instanceof SyntaxError || (error as any)?.name === 'SyntaxError';
    return serializeResult(createResult(
      getSafeMessageContext(msg),
      'return',
      undefined,
      error,
      isMalformedJSON ? CODE_INTERNAL_ERROR : CODE_BAD_REQUEST,
      isMalformedJSON ? ERRNO_INVALID_MESSAGE : ERRNO_INVALID_PARAMS,
    ));
  }
  try {
    const internalValue = invokeInternal(message);
    if (internalValue !== NO_INTERNAL_HANDLER) {
      return serializeResult(createResult(message, 'return', internalValue));
    }
    const target = resolveTarget(message);
    if (!target || !target.supportsSync) {
      const error = new Error(`KSI API ${message.scope || 'default'}.${message.name} is not synchronously available in browser`);
      return serializeResult(createResult(message, 'return', undefined, error, CODE_NOT_FOUND, ERRNO_UNSUPPORTED));
    }
    let callbackCalled = false;
    let callbackValue: any;
    let callbackError: any;
    const returnValue = target.call(
      (value) => { callbackCalled = true; callbackValue = value; },
      (error) => { callbackCalled = true; callbackError = error; },
    );
    if (callbackError) throw callbackError;
    if (target.callbackBased && callbackCalled) {
      return serializeResult(createResult(message, 'return', callbackValue));
    }
    if (returnValue && typeof returnValue.then === 'function') {
      throw new Error(`KSI API ${message.name} returned a Promise during a synchronous invocation`);
    }
    if (target.callbackBased && !callbackCalled) {
      throw new Error(`KSI API ${message.name} did not complete synchronously`);
    }
    return serializeResult(createResult(message, 'return', returnValue));
  } catch (error) {
    return serializeResult(createResult(message, 'return', undefined, error));
  }
}
const KSIModule: Spec = { invoke, invokeSync, setMapping };
export default KSIModule;
